<script setup lang="ts">
import { computed, inject, onMounted, watch, ref } from "vue";
import { BigNumber } from "ethers";
import { useI18n } from "vue-i18n";
import Stat from "~/components/stats/Stat.vue";
import useConvert from "~/composables/useConvert";
import DetailsSection from "~/components/card-details/DetailsSection.vue";
import PeriodSelector from "~/components/card-details/PeriodSelector.vue";
import ScrollableLineTimeChart from "~/components/charts/ScrollableLineTimeChart.vue";
import { PriceTimeframe } from "~/components/card-details/types/StatsTimeframes";
import { useCardDetails } from "~/components/card-details/use-card-details";
import { storeToRefs } from "pinia";
import { MarketSortType, MarketType, NftSkill, Skill, SocketEventType } from "fungi-types";
import { auctionApi } from "~/api/auction";
import { SalesHistoryResponse } from "~/api/auction/types";
import { formatDate } from "~/utils/dates";
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import { useStoreApp } from "~/stores/storeApp";
import socketService from "~/services/socketService";

const { weiToMatic, getAuctionAmount } = useConvert;
const props = defineProps<{
  selectedCard?: NftSkill;
  sales: "ongoing" | "history";
  title: string;
}>();

const { t } = useI18n();

const cardDetails = useCardDetails();
const { priceHistory, selectedSkill } = storeToRefs(cardDetails);

const priceStats = computed(() => {
  const average = weiToMatic(priceHistory.value.stats.average);
  const last =
    priceHistory.value.data.length != 0
      ? weiToMatic(BigNumber.from(priceHistory.value.data.at(0)!.price))
      : null;

  return { average, last };
});

const priceHistoryData = computed(() => {
  return priceHistory.value.data
    .map((dataPoint) => ({
      date: new Date(dataPoint.timestamp),
      value: weiToMatic(dataPoint.price),
    }))
    .reverse();
});

const priceHistoryLabels = computed(() => {
  return priceHistory.value.data.map(({ timestamp }) => timestamp).reverse();
});

const pricePeriodOptions = computed(() => [
  { label: `1 ${t("cardDetails.periods.m", "m")}`, value: { month: 1 } },
  { label: `3 ${t("cardDetails.periods.m", "m")}`, value: { months: 3 } },
  { label: `1 ${t("cardDetails.periods.y", "y")}`, value: { years: 1 } },
  { label: t("cardDetails.periods.all", "All"), value: undefined },
]);

const skillsColors = inject<Record<Skill, string>>("skills-colors")!;
const skillsGradients = inject<Record<Skill, [string, string]>>("skills-gradients")!;
const sales = ref<SalesHistoryResponse["result"]>([]);

const getSales = async () => {
  const params = {
    tennisPlayerIds: props.selectedCard ? [props.selectedCard.tennisPlayerId] : [],
    skills: props.selectedCard && selectedSkill.value ? [selectedSkill.value] : undefined,
    scarcities: props.selectedCard ? [props.selectedCard.scarcity] : [],
    limit: 10,
    sort: MarketSortType.ENDING_DESC,
    seasons: props.selectedCard ? [Number(props.selectedCard.season)] : undefined,
  };
  if (props.sales === "history") {
    const response = await auctionApi.sales.history.list.query(params);
    sales.value = response.result;
  } else if (props.sales === "ongoing") {
    const response = await auctionApi.sales.ongoing.list.query(params);
    sales.value = response.result;
    // const response = await auctionApi.sales.ongoing.list.query(params);
  }
};

socketService.on(SocketEventType.AUCTION_UPDATED, (event) => {
  if (sales.value.find((sale) => sale.auction?.cuid === event.auction.cuid)) {
    getSales();
  }
});

watch(
  () => [props.selectedCard, selectedSkill.value],
  () => {
    getSales();
  },
  { immediate: true }
);

onMounted(() => {
  getSales();
});

const cols = computed(() => {
  const data = [
    {
      key: "seller",
      label: t("_shared.seller"),
      class: "w-fit w-0",
    },
    {
      key: "buyer",
      label: t("_shared.buyer"),
      class: "w-fit w-0",
    },
    {
      key: "details",
      label: t("_shared.details"),
    },
    {
      key: "price ($)",
      label: t("_shared.price") + " ($)",
      class: "text-right",
    },
    {
      key: "date",
      label: t("_shared.date"),
      class: "text-right",
    },
    {
      key: "deadline",
      label: t("_shared.deadline"),
      class: "text-right",
    },
    {
      key: "cta",
      label: "",
      class: "text-right",
    },
  ];
  if (props.sales === "ongoing") {
    return data.filter((col) => col.key !== "date" && col.key !== "buyer");
  } else {
    return data.filter((col) => col.key !== "deadline" && col.key !== "cta");
  }
});

function bidOnCard(nft: NftSkill) {
  useStoreMarketplace().selectedItem = nft;
  useStoreApp().setBidModalState(true);
}

function buyItem(nft: NftSkill) {
  useStoreMarketplace().selectedItem = nft;
  useStoreApp().setBidModalState(true);
}

const bidOrBuy = (sale: NftSkill) => {
  if (sale.auction?.marketType === MarketType.SECONDARY) {
    bidOnCard(sale);
  } else {
    buyItem(sale);
  }
};
</script>

<template>
  <DetailsSection :name="title">
    <template #default>
      <div
        class="rounded-md md:bg-black w-full md:border md:border-slate-700 md:overflow-hidden md:p-3 pb-1 overflow-auto"
        :class="{ 'bg-black border-slate-700 border': sales.length === 0 }"
      >
        <div v-if="sales.length > 0">
          <div class="hidden md:block w-full overflow-x-auto">
            <table class="w-full table-auto">
              <thead>
                <tr class="text-xs">
                  <th
                    v-for="col in cols"
                    :key="col.key"
                    class="border-b border-b-neutral-900 pb-2 text-left font-normal uppercase text-neutral-400 p-2"
                    :class="col.class"
                  >
                    {{ t(`${col.label}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(sale, index) in sales"
                  :key="sale.auction?.cuid"
                  class="text-sm"
                  :class="{ 'border-b border-neutral-900 group': index + 1 < sales.length }"
                >
                  <td
                    v-for="col in cols"
                    class="p-2 py-3 group-hover:bg-neutral-800"
                    :key="col.key"
                    :class="col.class"
                  >
                    <template v-if="col.key === 'seller'">
                      {{ sale.auction?.seller.username || "-" }}
                    </template>
                    <template v-else-if="col.key === 'buyer'">
                      {{ sale.auction?.lastBidder?.username || "-" }}
                    </template>
                    <template v-else-if="col.key === 'details'">
                      <div class="flex gap-1">
                        <BonusChip
                          class="!bg-neutral-900"
                          :xp="(sale as NftSkill).xp"
                          :season="(sale as NftSkill).season"
                          :scarcity="(sale as NftSkill).scarcity"
                          :bgColor="'bg-neutral-900'"
                        />

                        <div
                          class="bg-neutral-900 rounded-md flex flex-row px-2 items-center gap-1 text-xs uppercase py-1"
                        >
                          <img
                            width="15px"
                            :src="`/images/${(sale as NftSkill).courtType}_court.png`"
                            alt="courtType"
                          />
                          {{ (sale as NftSkill).courtType }}
                        </div>
                      </div>
                    </template>
                    <template v-else-if="col.key === 'price ($)'">
                      <div class="flex items-center justify-end gap-1">
                        <span>{{
                          getAuctionAmount({
                            scarcity: props.selectedCard?.scarcity!,
                            marketType:
                              sale?.auction?.marketType === MarketType.SECONDARY
                                ? MarketType.SECONDARY
                                : MarketType.PRIMARY,
                            usdcAmount:
                              sale?.auction?.lastBidAmountUsdc ||
                              sale?.auction?.startUsdcAmount ||
                              "0",
                            weiAmount:
                              sale?.auction?.lastBidAmount || sale?.auction?.startedAmount || "0",
                          }).fiat.usd
                        }}</span>
                      </div>
                    </template>
                    <template v-else-if="col.key === 'date'">
                      {{ new Date(sale.auction?.endedAt || new Date()).toLocaleDateString() }}
                    </template>
                    <template v-else-if="col.key === 'deadline'">
                      {{ formatDate(sale.auction?.endedAt || new Date(), "dd MMM HH:m:s") }}
                    </template>
                    <template v-else-if="col.key === 'cta'">
                      <div
                        @click="bidOrBuy(sale as NftSkill)"
                        class="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded-full cursor-pointer text-center font-normal"
                      >
                        <template v-if="sale.auction?.marketType === MarketType.SECONDARY">
                          {{ t("_payment.buy") }}
                        </template>
                        <template v-else>
                          {{ t("_payment.bid") }}
                        </template>
                      </div>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="md:hidden flex gap-2">
            <div
              v-for="sale in sales"
              :key="sale.auction?.cuid"
              class="text-sm bg-black w-full border border-slate-700 rounded-md p-2"
            >
              <div
                class="flex justify-between p-1.5 items-center min-w-[300px]"
                v-for="(col, index) in cols"
                :class="{ 'border-b border-neutral-900': index + 1 < cols.length }"
                :key="col.key"
              >
                <div v-if="col.key !== 'cta'" class="uppercase text-neutral-400 text-xs">
                  {{ col.label }}
                </div>
                <div
                  :class="{
                    'flex justify-center w-full pt-2': col.key === 'cta',
                  }"
                >
                  <template v-if="col.key === 'seller'">
                    {{ sale.auction?.seller.username || "-" }}
                  </template>
                  <template v-else-if="col.key === 'buyer'">
                    {{ sale.auction?.lastBidder?.username || "-" }}
                  </template>
                  <template v-else-if="col.key === 'details'">
                    <div class="flex gap-1">
                      <BonusChip
                        class="!bg-neutral-900"
                        :xp="(sale as NftSkill).xp"
                        :season="(sale as NftSkill).season"
                        :scarcity="(sale as NftSkill).scarcity"
                        :bgColor="'bg-neutral-900'"
                      />

                      <div
                        class="bg-neutral-900 rounded-md flex flex-row px-2 items-center gap-1 text-xs uppercase py-1"
                      >
                        <img
                          width="15px"
                          :src="`/images/${(sale as NftSkill).courtType}_court.png`"
                          alt="courtType"
                        />
                        {{ (sale as NftSkill).courtType }}
                      </div>
                    </div>
                  </template>
                  <template v-else-if="col.key === 'price ($)'">
                    <div class="flex items-center justify-end gap-1">
                      <span>{{
                        getAuctionAmount({
                          scarcity: props.selectedCard?.scarcity!,
                          marketType:
                            sale?.auction?.marketType === MarketType.SECONDARY
                              ? MarketType.SECONDARY
                              : MarketType.PRIMARY,
                          usdcAmount: sale?.auction?.lastBidAmountUsdc || "0",
                          weiAmount:
                            sale?.auction?.lastBidAmount || sale?.auction?.startUsdcAmount || "0",
                        }).fiat.usd
                      }}</span>
                    </div>
                  </template>
                  <template v-else-if="col.key === 'date'">
                    {{ new Date(sale.auction?.endedAt || new Date()).toLocaleDateString() }}
                  </template>
                  <template v-else-if="col.key === 'deadline'">
                    {{ formatDate(sale.auction?.endedAt || new Date(), "dd MMM HH:m:s") }}
                  </template>
                  <template v-else-if="col.key === 'cta'">
                    <div
                      @click="bidOrBuy(sale as NftSkill)"
                      class="bg-blue-600 hover:bg-blue-700 text-white text-xs py-1 rounded-full cursor-pointer text-center font-normal px-10"
                    >
                      <template v-if="sale.auction?.marketType === MarketType.SECONDARY">
                        {{ t("_payment.buy") }}
                      </template>
                      <template v-else>
                        {{ t("_payment.bid") }}
                      </template>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-6 text-neutral-500 text-sm md:text-base">
          <template v-if="props.sales === 'ongoing'">
            {{ t("cardDetails.ongoing-sales.noOngoingSales") }}
          </template>
          <template v-else-if="props.sales === 'history'">
            {{ t("cardDetails.history-sales.noSales") }}
          </template>
        </div>
      </div>
    </template>
  </DetailsSection>
</template>
